import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirmar } from "@/components/ConfirmDialog";
import { Loader2 } from "lucide-react";

const SIT = {
  pronta: ["Pronta", "bg-green-50 text-green-700"],
  sem_cidade: ["Sem cidade nem CEP", "bg-amber-50 text-amber-700"],
  ja_tem_salao: ["Já tem salão", "bg-slate-100 text-slate-500"],
  conta_bloqueada: ["Conta bloqueada", "bg-red-50 text-red-700"],
  sem_conta: ["Nunca ativou a conta", "bg-slate-100 text-slate-500"],
};

/** Admin monta o salão das certificadas do Magistral (nome, cidade do cadastro/CEP, foto do perfil e 3 serviços) e põe na vitrine. */
export default function MontarVitrine({ aberto, fechar, aoCriar }) {
  const [d, setD] = useState(null);
  const [marcadas, setMarcadas] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (!aberto) return;
    setD(null); setRes(null);
    api.get("/admin/salao/pre-cadastro").then((r) => {
      setD(r.data);
      setMarcadas(Object.fromEntries(r.data.pessoas.filter((p) => p.situacao === "pronta").map((p) => [p.user_id, true])));
    }).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); fechar(); });
  }, [aberto]); // eslint-disable-line react-hooks/exhaustive-deps

  const ids = Object.keys(marcadas).filter((k) => marcadas[k]);
  const criar = async () => {
    if (!(await confirmar(`Montar ${ids.length} salões e colocar na vitrine agora? Elas ficam liberadas no IEP Salão e as clientes já veem o botão do WhatsApp delas. Nenhuma mensagem é enviada.`))) return;
    setSalvando(true);
    try {
      const r = await api.post("/admin/salao/pre-cadastro", { user_ids: ids });
      setRes(r.data); toast.success(`${r.data.criados} salões montados.`); aoCriar?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSalvando(false); }
  };

  const cont = (s) => (d?.pessoas || []).filter((p) => p.situacao === s).length;
  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && fechar()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Montar a vitrine com as certificadas do Magistral</DialogTitle></DialogHeader>
        {!d ? <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : res ? (
          <div className="space-y-2 text-sm">
            <p><b>{res.criados}</b> salões montados. Confira em iepplataforma.com/profissionais.</p>
            <ul className="divide-y divide-slate-100 rounded-lg border">{res.resultado.map((x) => (
              <li key={x.user_id} className="flex justify-between gap-3 px-3 py-2"><span>{x.nome || x.user_id}</span>
                <span className={x.ok && x.situacao === "criado" ? "text-green-700" : "text-amber-700"}>{x.ok ? (x.situacao === "criado" ? `ok${x.foto === "ok" ? " · com foto" : ""}${x.falta?.length ? ` · falta: ${x.falta.join(" ")}` : ""}` : "já tinha salão") : x.erro}</span></li>))}</ul>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">Cada salão sai com o nome dela, a cidade do cadastro (ou do CEP), a foto do perfil (se tiver) e os serviços <b>{d.servicos.join(", ")}</b>, com valor sob consulta. A cliente fala direto no WhatsApp dela até ela entrar e ligar a agenda. Quem já tem salão não é mexido.</p>
            <div className="flex flex-wrap gap-2 text-xs">{Object.entries(SIT).map(([k, [t, c]]) => <span key={k} className={`rounded-full px-2 py-0.5 font-semibold ${c}`}>{t}: {cont(k)}</span>)}</div>
            <div className="overflow-x-auto rounded-lg border"><table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="w-8" /><th className="px-2 py-2 text-left">Aluna</th><th className="px-2 py-2 text-left">Cidade</th><th className="px-2 py-2 text-left">Foto</th><th className="px-2 py-2 text-left">Situação</th></tr></thead>
              <tbody className="divide-y divide-slate-100">{d.pessoas.map((p) => (
                <tr key={p.student_id} className={p.situacao === "pronta" ? "" : "text-slate-400"}>
                  <td className="px-2 text-center">{p.situacao === "pronta" && <input type="checkbox" checked={!!marcadas[p.user_id]} onChange={(e) => setMarcadas({ ...marcadas, [p.user_id]: e.target.checked })} />}</td>
                  <td className="px-2 py-1.5">{p.nome}</td>
                  <td className="px-2 py-1.5">{p.cidade ? `${p.bairro ? p.bairro + " · " : ""}${p.cidade}${p.uf ? "/" + p.uf : ""}` : "—"}{p.cidade_pelo_cep && <span className="ml-1 text-[10px] text-slate-400">(CEP)</span>}</td>
                  <td className="px-2 py-1.5">{p.foto ? "sim" : "—"}</td>
                  <td className="px-2 py-1.5"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${SIT[p.situacao]?.[1]}`}>{SIT[p.situacao]?.[0]}{p.salao ? ` · ${p.salao}` : ""}</span></td>
                </tr>))}</tbody>
            </table></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={fechar}>{res ? "Fechar" : "Cancelar"}</Button>
          {d && !res && <Button disabled={salvando || !ids.length} className="text-white" style={{ background: "#1E9E2A" }} onClick={criar}>{salvando ? "Montando…" : `Montar ${ids.length} salões`}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
